(() => {
  'use strict';
  if (!window.customElements || customElements.get('shopsta-localization')) return;
  const flag = code => /^[A-Z]{2}$/.test(code) ? String.fromCodePoint(...[...code].map(char => char.charCodeAt(0) + 127397)) : '';
  const normalize = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    node.className = className;
    if (text) node.textContent = text;
    return node;
  };

  class ShopstaLocalization extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      const options = {signal: this.listeners.signal};
      const form = this.querySelector('form');
      const disclosures = [...this.querySelectorAll('details')];
      if (!form || !disclosures.length || typeof form.requestSubmit !== 'function') return;
      // Reconnect safely when the drawer is moved/rebuilt by the theme editor.
      this.querySelectorAll('[data-slc-generated]').forEach(node => node.remove());
      const selection = () => ['country_code', 'language_code'].map(name => form.elements.namedItem(name)?.value || '').join('|');
      const initialSelection = selection();
      const mobileMenu = this.classList.contains('slc--inline') ? this.closest('.smm') : null;
      const syncMobilePanel = () => {
        if (!mobileMenu) return;
        const open = disclosures.some(disclosure => disclosure.open);
        mobileMenu.classList.toggle('smm--localizing', open);
        if (open) {
          const masthead = mobileMenu.querySelector('.smm__masthead');
          this.style.setProperty('--slc-menu-top', `${masthead?.offsetHeight || 80}px`);
        }
      };
      const close = (disclosure, focus = false) => {
        if (!disclosure.open) return;
        disclosure.open = false;
        syncMobilePanel();
        if (focus) disclosure.querySelector('summary').focus({preventScroll: true});
      };
      const closeAll = () => disclosures.forEach(disclosure => close(disclosure));
      const syncChoices = () => disclosures.forEach(disclosure => {
        const selected = form.elements.namedItem(disclosure.dataset.slcField)?.value;
        disclosure.querySelectorAll('[data-slc-value]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.slcValue === selected)));
      });
      const reset = event => {
        if (event?.persisted) { form.reset(); closeAll(); }
        this.pending = false;
        this.inert = false;
        this.removeAttribute('aria-busy');
        syncChoices();
      };
      reset();

      form.addEventListener('change', event => {
        if (!event.target.matches('select[name="country_code"], select[name="language_code"]')) return;
        if (!this.pending && selection() !== initialSelection) form.requestSubmit();
      }, options);
      form.addEventListener('submit', event => {
        if (event.defaultPrevented || this.pending || selection() === initialSelection) {
          event.preventDefault();
          return;
        }
        const destination = new URL(window.location.href);
        const routingParams = new Set(['country', 'country_code', 'currency', 'language', 'language_code', 'locale', 'lang', 'redirect', 'return_to', 'shpxid']);
        for (const key of [...destination.searchParams.keys()]) {
          if (routingParams.has(key.toLowerCase())) destination.searchParams.delete(key);
        }
        const returnTo = form.elements.namedItem('return_to');
        if (returnTo) returnTo.value = destination.pathname + destination.search + destination.hash;
        this.pending = true;
        this.inert = true;
        this.setAttribute('aria-busy', 'true');
        queueMicrotask(() => { if (event.defaultPrevented) reset(); });
      }, options);

      disclosures.forEach(disclosure => {
        const select = disclosure.querySelector('select');
        const panel = disclosure.querySelector('.slc__panel');
        let built = false;
        const build = () => {
          if (built) return;
          built = true;
          const country = select.name === 'country_code';
          const content = element('div', 'slc__enhanced');
          content.dataset.slcGenerated = '';
          const choices = element('div', 'slc__choices');
          const rows = [];
          if (country) {
            const search = element('input', 'slc__search');
            search.type = 'search';
            search.placeholder = this.dataset.searchLabel + '…';
            search.setAttribute('aria-label', this.dataset.searchLabel);
            search.autocomplete = 'off';
            const empty = element('p', 'slc__empty', this.dataset.noResults);
            empty.hidden = true;
            empty.setAttribute('role', 'status');
            search.addEventListener('input', () => {
              const query = normalize(search.value.trim());
              let count = 0;
              rows.forEach(({button, terms}) => { button.hidden = !terms.includes(query); if (!button.hidden) count++; });
              empty.hidden = count > 0;
            }, options);
            // Enter in the search must not submit a country that wasn't chosen.
            search.addEventListener('keydown', event => {
              if (event.key === 'Enter') event.preventDefault();
              if (event.key === 'ArrowDown') { event.preventDefault(); rows.find(row => !row.button.hidden)?.button.focus(); }
            }, options);
            content.append(search, empty);
          }
          for (const option of [...select.options].sort((a, b) => Number(b.selected) - Number(a.selected))) {
            const button = element('button', 'slc__option');
            button.type = 'button';
            button.dataset.slcValue = option.value;
            button.setAttribute('aria-pressed', String(option.selected));
            if (country) {
              const icon = element('span', 'slc__flag', flag(option.value));
              icon.setAttribute('aria-hidden', 'true');
              button.append(icon);
            } else if (option.lang) button.lang = option.lang;
            button.append(element('span', 'slc__option-name', option.dataset.name || option.textContent));
            if (country) button.append(element('span', 'slc__option-currency', option.dataset.currency));
            const check = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            check.setAttribute('class', 'slc__check');
            check.setAttribute('viewBox', '0 0 20 20');
            check.setAttribute('aria-hidden', 'true');
            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('d', 'm4 10 4 4 8-8'); check.append(path); button.append(check);
            button.addEventListener('click', () => {
              if (this.pending) return;
              if (select.value === option.value) { close(disclosure, true); return; }
              select.value = option.value;
              form.requestSubmit();
            }, options);
            rows.push({button, terms: normalize(option.textContent + ' ' + option.value)});
            choices.append(button);
          }
          choices.addEventListener('keydown', event => {
            if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
            const visible = rows.filter(row => !row.button.hidden).map(row => row.button);
            const index = visible.indexOf(event.target);
            if (index < 0) return;
            event.preventDefault();
            const next = event.key === 'Home' ? 0 : event.key === 'End' ? visible.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + visible.length) % visible.length;
            visible[next]?.focus();
          }, options);
          content.append(choices); panel.append(content);
        };
        disclosure.addEventListener('toggle', () => {
          if (!disclosure.open) { syncMobilePanel(); return; }
          disclosures.filter(other => other !== disclosure).forEach(other => close(other));
          build(); syncChoices();
          syncMobilePanel();
          // Focus the panel heading control without summoning the phone keyboard.
          if (mobileMenu) disclosure.querySelector('[data-slc-close]')?.focus({preventScroll: true});
        }, options);
        disclosure.querySelector('[data-slc-close]')?.addEventListener('click', () => close(disclosure, true), options);
        if (disclosure.open) build();
      });
      this.addEventListener('keydown', event => {
        const open = disclosures.find(disclosure => disclosure.open);
        if (event.key === 'Tab') {
          // Only keyboard navigation should close on a focus change. Touch browsers
          // can move focus to the dialog/body before dispatching an option's click.
          window.setTimeout(() => {
            if (this.isConnected && !this.contains(document.activeElement)) closeAll();
          }, 0);
        }
        if (event.key === 'Escape' && open) {
          event.preventDefault(); event.stopPropagation(); close(open, true);
        }
      }, options);
      document.addEventListener('click', event => { if (!this.contains(event.target)) closeAll(); }, options);
      window.addEventListener('pageshow', reset, options);
      mobileMenu?.addEventListener('close', closeAll, options);
      syncMobilePanel();
      this.setAttribute('data-enhanced', '');
    }
    disconnectedCallback() {
      this.listeners?.abort();
      this.removeAttribute('data-enhanced'); this.removeAttribute('aria-busy');
      this.inert = false; this.pending = false;
    }
  }
  customElements.define('shopsta-localization', ShopstaLocalization);
})();
